// History, top to bottom: open deals, past deals, the account's Cartesia orgs with their usage
// trend, every touch as one filterable timeline, and -- least important, so last -- the
// Salesforce customer journeys, in the same compact rows as the deals (detail on hover).
import { useState } from 'react';
import type { BookAccount, Opportunity } from '../api/types';
import { CardRows, CardTitle, HoverCard } from '../components/HoverCard';
import { contactDisplayName } from '../lib/contactName';
import { formatCredits, formatMoney } from '../lib/format';
import {
  TOUCH_FILTERS, dealOutcome, emptyHistoryLine, filterTouches, openDeals, orgTrends, pastDeals, touchLabel, touchTimeline,
} from './historyData';
import type { OrgTrend, Touch, TouchKind } from './historyData';
import { outreachPeople } from './outreachPeople';
import { EnvelopeIcon, PanelSection } from './panelParts';
import { journeysBlock } from './RecordsBlock';
import { dealAmount } from './panelFacts';

function DealRow({ deal, past }: { deal: Opportunity; past: boolean }) {
  // The one amount rule (`dealAmount`): Salesforce Amount, else qualified pipeline, else "—".
  const figure = dealAmount(deal);
  const amount = figure == null ? '—' : formatMoney(figure);
  const why = deal.loss_reason || deal.disqualification_reason;
  // Who it was with and the free-text reason sit on hover. An activity-derived contact is labelled.
  const detailRows: Array<[string, string]> = [];
  if (deal.contact?.name || deal.contact?.email) {
    const who = contactDisplayName(deal.contact.name, deal.contact.email, '');
    detailRows.push(['With', deal.contact.source === 'activity' ? `${who} (from activity)` : who]);
  }
  if (deal.created) detailRows.push(['Created', deal.created]);
  const name = <span className="dealname">{deal.name || 'Unnamed deal'}</span>;
  return (
    <div className={'dealrow' + (past && deal.won ? ' won' : '')}>
      {detailRows.length || deal.loss_details ? (
        <HoverCard className="dealnametrigger" label={`About ${deal.name || 'this deal'}`} card={() => (
          <>
            <CardTitle>{deal.name || 'Deal'}</CardTitle>
            {detailRows.length ? <CardRows rows={detailRows} /> : null}
            {deal.loss_details ? <p className="hcprose">{deal.loss_details}</p> : null}
          </>
        )}>{name}</HoverCard>
      ) : name}
      <span className="dealamount">{amount}</span>
      <span className="dealmeta">
        {past ? dealOutcome(deal) : deal.stage || 'No stage'}
        {!past && deal.contact?.name ? ` · ${deal.contact.name}` : ''}
        {why ? ` · ${why}` : ''}
      </span>
      <span className="dealdate">{deal.close_date ? `${past ? 'closed' : 'closes'} ${deal.close_date}` : 'no close date'}</span>
    </div>
  );
}

/** Prior 30 days to the last 30, as a two-point line scaled to the busier of the two. */
function TrendLine({ points }: { points: [number, number] }) {
  const highest = Math.max(points[0], points[1], 1);
  const heights = points.map(point => 17 - (point / highest) * 14);
  const rising = points[1] >= points[0];
  return (
    <svg className={'trendline' + (rising ? ' up' : ' down')} width="44" height="20" viewBox="0 0 44 20" aria-hidden="true">
      <polyline points={`4,${heights[0]} 40,${heights[1]}`} fill="none" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="40" cy={heights[1]} r="2.2" />
    </svg>
  );
}

export function OrgRow({ trend }: { trend: OrgTrend }) {
  const { org } = trend;
  const paid = Boolean(org.tier) && org.tier !== 'free';
  return (
    <div className="orgrow">
      <span className={'orgbadge' + (paid ? '' : ' playground')}>{paid ? 'ORG' : 'PG'}</span>
      <span className="orgname">
        {trend.name}
        <span className="muted"> · {org.tier || 'no plan'}{trend.users ? ` · ${trend.users} ${trend.users === 1 ? 'person' : 'people'} here` : ''}</span>
        {org.live === false ? <span className="muted"> · no longer in the product</span> : null}
      </span>
      <span className="orgusage" title="Credits, last 30 days">{formatCredits(org.credits_30)}</span>
      <TrendLine points={trend.points} />
      <span className={'orgchange' + (trend.changePct != null && trend.changePct < 0 ? ' down' : '')}>
        {trend.changePct == null ? '' : `${trend.changePct > 0 ? '+' : ''}${Math.round(trend.changePct)}%`}
      </span>
    </div>
  );
}

function TouchIcon({ kind }: { kind: TouchKind }) {
  if (kind === 'email') return <span className="touchicon email"><EnvelopeIcon /></span>;
  if (kind === 'linkedin') return <span className="touchicon linkedin">in</span>;
  if (kind === 'call') return <span className="touchicon call">☏</span>;
  if (kind === 'meeting') return <span className="touchicon meeting">◷</span>;
  return <span className="touchicon">•</span>;
}

export function TouchRow({ touch }: { touch: Touch }) {
  const what = touchLabel(touch);
  const body = (
    <>
      <TouchIcon kind={touch.kind} />
      <span className="touchwho">{touch.direction === 'in' ? '↙ ' : ''}{touch.who}</span>
      <span className="touchwhat">{what}</span>
      <span className="touchby">{touch.by || ''}</span>
      <span className="touchday">{touch.day}</span>
    </>
  );
  // A Gong meeting's summary and next steps are on hover.
  if (!touch.gong) return <div className="touchrow">{body}</div>;
  const call = touch.gong;
  return (
    <HoverCard className="touchrow" label={`Meeting ${call.title || ''}`} card={() => (
      <>
        <CardTitle>{call.title || 'Meeting'}</CardTitle>
        <CardRows rows={[
          ['When', call.day || '—'],
          ['Host', call.host || '—'],
          ['Length', call.duration_seconds ? `${Math.round(call.duration_seconds / 60)} min` : '—'],
        ]} />
        {call.brief[0] ? <p className="hcprose">{call.brief[0]}</p> : null}
        {call.next_steps.length ? <p className="hcprose"><b>Next:</b> {call.next_steps.join(' · ')}</p> : null}
      </>
    )}>
      {body}
    </HoverCard>
  );
}

export function HistoryTab({ row }: { row: BookAccount }) {
  const [filter, setFilter] = useState<TouchKind | 'all'>('all');
  const open = openDeals(row);
  const past = pastDeals(row);
  const trends = orgTrends(row, outreachPeople(row));
  const touches = touchTimeline(row);
  const shown = filterTouches(touches, filter);
  const journeys = journeysBlock(row.journeys);

  const emptyLine = emptyHistoryLine({
    open: open.length, past: past.length, orgs: trends.length, touches: touches.length, journeys: row.journeys.length,
  });

  return (
    <>
      {emptyLine ? <p className="panelempty historyempty">{emptyLine}</p> : null}

      {open.length ? (
        <PanelSection title="Open opportunities">
          {open.map((deal, index) => <DealRow key={index} deal={deal} past={false} />)}
        </PanelSection>
      ) : null}

      {past.length ? (
        <PanelSection title="Past opportunities">
          {past.map((deal, index) => <DealRow key={index} deal={deal} past />)}
        </PanelSection>
      ) : null}

      {trends.length ? (
        <PanelSection title="Cartesia orgs" aside="credits, last 30 days vs the 30 before">
          {trends.map(trend => <OrgRow key={trend.org.name} trend={trend} />)}
        </PanelSection>
      ) : null}

      {/* The list is the latest touches, capped per account; the counts cover the full history. */}
      {touches.length ? (
        <PanelSection title="Touches" aside={`${row.emails} emails · ${row.calls} calls · ${row.linkedin} LinkedIn · ${row.responses} replies`}>
          <div className="touchfilters" role="group" aria-label="Show touches">
            {TOUCH_FILTERS.map(option => (
              <button key={option.kind} type="button" className={filter === option.kind ? 'on' : ''} aria-pressed={filter === option.kind}
                onClick={() => setFilter(option.kind)}>
                {option.label}
              </button>
            ))}
          </div>
          {shown.length ? shown.map((touch, index) => <TouchRow key={index} touch={touch} />) : <p className="panelempty">No {TOUCH_FILTERS.find(option => option.kind === filter)?.label.toLowerCase()} recorded.</p>}
        </PanelSection>
      ) : null}

      {row.journeys.length ? (
        <PanelSection title={`Customer journeys (${row.journeys.length})`} aside={journeys.hint}>
          {journeys.body ?? <p className="panelempty">No customer journey in Salesforce.</p>}
        </PanelSection>
      ) : null}

      <p className="tabsource">Sources: Salesforce, Gong, product usage.</p>
    </>
  );
}
